import type { Chat } from './api'
import { laneStatusFriendly } from './laneStatus'

// `hasPendingOutbox` marks a chat with an undelivered message in the local
// outbox. The server cannot see that work (no queued row exists yet), so the
// caller supplies it: the chat is live and a lane holding one is not free.
// Done includes unread idle replies; active work stays in Live only.
export function matchesDoneChatFilter(chat: Chat, hasPendingOutbox: boolean = false): boolean {
  if (chat.pending_approval) return true
  // Working belongs in Live, never Done. Unread idle lanes still surface in
  // Done so attention is not lost (including when snoozed).
  if (hasActiveChatWork(chat, hasPendingOutbox)) return false
  if ((chat.unread ?? 0) > 0) return true
  if (isSnoozedChat(chat)) return false
  const latestResetAt = chat.last_reset_at ?? 0
  const latestAgentReplyAt = chat.last_agent_reply_at ?? 0
  const latestMessageIsAgentReply =
    chat.last_message_direction === 'outbound' || chat.last_message_direction === 'agent'
  return (
    !hasActiveChatWork(chat, hasPendingOutbox)
    && latestAgentReplyAt > latestResetAt
    && latestMessageIsAgentReply
  )
}

export function hasActiveChatWork(chat: Chat, hasPendingOutbox: boolean = false): boolean {
  return hasPendingOutbox
    || chat.lane_status === 'busy'
    || chat.active_work_depth > 0
    || (chat.queue_depth ?? 0) > 0
    || (chat.scheduled_rechecks?.length ?? 0) > 0
}

export function isSnoozedChat(chat: Chat): boolean {
  return chat.type !== 'auto-spawn'
    && !!chat.snoozed_until
    && chat.snoozed_until > Math.floor(Date.now() / 1000)
}

export function isFreeChat(chat: Chat, liveStream: boolean = true, hasPendingOutbox: boolean = false): boolean {
  const latestResetAt = chat.last_reset_at ?? 0
  if (chat.type === 'auto-spawn') return false
  if (isSnoozedChat(chat)) return false
  if (hasPendingOutbox) return false

  const latestAgentReplyAt = chat.last_agent_reply_at ?? 0
  const latestActivityAt = chat.last_activity_at ?? 0
  const laneStatus = laneStatusFriendly(chat.lane_status, chat.lane_last_heartbeat, {}, liveStream)
  // A synthetic list-sort timestamp is not evidence that Mark used a lane.
  // Newly introduced lanes can be surfaced near the top before they have any
  // transcript rows; no reset, reply, or message direction is the durable
  // never-used signal.
  const hasNeverBeenUsed =
    latestResetAt <= 0
    && latestAgentReplyAt <= 0
    && chat.last_message_direction == null

  return (
    laneStatus.tone === 'green'
    && !hasActiveChatWork(chat)
    && (
      hasNeverBeenUsed
      || (
        latestResetAt > 0
        && latestAgentReplyAt <= latestResetAt
        && latestActivityAt <= latestResetAt
      )
    )
  )
}
